'use client';

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { QrCode } from 'lucide-react';
import { asObject, type FieldValue } from '../formFieldTypes';

// Lazy load the heavy QRScannerModal component (pulls in html5-qrcode, SmartTask viewport, etc.)
const LazyQRScannerModal = React.lazy(() =>
  import('@/app/client-zone/modules/total-operations/components/smart-task/components/QRScannerModal').then((m) => ({
    default: m.default,
  }))
);

/**
 * BarcodeScannerField — QR/Barcode scanner using camera or manual entry
 *
 * Uses the BarcodeDetector API where available (Chrome/Edge/Android),
 * falls back to manual text input.
 * When the enhanced QRScannerModal is available, offers a richer scanning
 * experience with mode selection (job, component, asset, stock, receipt).
 *
 * options.formats          — Accepted barcode formats
 * options.lookup_endpoint  — API to translate scanned value into display data
 * options.show_value       — Display raw scanned value
 */

type BarcodeScannerOptions = {
      formats?: string[];
      lookup_endpoint?: string;
      show_value?: boolean;
      [key: string]: unknown;
};

interface BarcodeScannerFieldProps {
  field: {
    key: string;
    label: string;
    required?: boolean;
    readonly?: boolean;
    placeholder?: string;
    options?: unknown;
    help_text?: string;
    [key: string]: unknown;
  };
  value: FieldValue; // { raw_value, format?, lookup_result?, timestamp }
  onChange: (value: FieldValue) => void;
  isDark: boolean;
}

interface ScanResult {
  raw_value: string;
  format?: string;
  lookup_result?: unknown;
  [key: string]: unknown;
  timestamp: string;
}

// BarcodeDetector API (not yet in all TS defs)
declare global {
  interface Window {
    BarcodeDetector?: new (options?: { formats: string[] }) => {
      detect: (source: ImageBitmap | HTMLVideoElement | HTMLCanvasElement) => Promise<{ rawValue: string; format: string }[]>;
    };
  }
}

export const BarcodeScannerField: React.FC<BarcodeScannerFieldProps> = ({ field, value, onChange, isDark }) => {
  const opts = (field.options ?? {}) as BarcodeScannerOptions;
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hasBarcodeAPI, setHasBarcodeAPI] = useState(() => typeof window !== 'undefined' && !!window.BarcodeDetector);
  const [showEnhancedScanner, setShowEnhancedScanner] = useState(false);
  const [enhancedMode, setEnhancedMode] = useState<'job' | 'component' | 'asset' | 'stock' | 'receipt'>('job');

  const scanResult: ScanResult | null = asObject(value)?.raw_value ? (value as ScanResult) : (typeof value === 'string' && value ? { raw_value: value, timestamp: new Date().toISOString() } : null);
  const showValue = opts?.show_value !== false;

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setScanning(false);
  }, []);

  const handleScan = useCallback(async (rawValue: string, format?: string) => {
    stopCamera();
    const result: ScanResult = { raw_value: rawValue, format, timestamp: new Date().toISOString() };

    // Lookup if endpoint provided
    if (opts?.lookup_endpoint) {
      try {
        const res = await fetch(opts.lookup_endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: rawValue, format }),
        });
        if (res.ok) {
          const data = await res.json();
          result.lookup_result = data.data || data;
        }
      } catch { /* lookup failed — still save raw value */ }
    }

    onChange(result);
  }, [opts?.lookup_endpoint, onChange, stopCamera]);

  const startScanning = useCallback(async () => {
    if (!hasBarcodeAPI) {
      setError('Camera scanning not supported — enter value manually');
      return;
    }

    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);

      const formats = opts?.formats || ['qr_code', 'code_128', 'ean_13', 'code_39'];
      const detector = new window.BarcodeDetector!({ formats });

      const detectLoop = async () => {
        if (!videoRef.current || !streamRef.current) return;
        try {
          const barcodes = await detector.detect(videoRef.current);
          if (barcodes.length > 0) {
            handleScan(barcodes[0].rawValue, barcodes[0].format);
            return;
          }
        } catch { /* detection frame error — continue */ }
        if (streamRef.current) requestAnimationFrame(detectLoop);
      };
      detectLoop();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to access camera');
      setScanning(false);
    }
  }, [hasBarcodeAPI, opts?.formats, handleScan]);

  const handleManualSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) {
      handleScan(manualInput.trim(), 'manual');
      setManualInput('');
    }
  }, [manualInput, handleScan]);

  const clearValue = useCallback(() => {
    onChange(null);
  }, [onChange]);

  // Enhanced QRScannerModal handler
  const handleEnhancedScan = useCallback((code: string) => {
    void handleScan(code, 'qr_code');
    setShowEnhancedScanner(false);
  }, [handleScan]);

  return (
    <div className="space-y-2">
      {scanResult ? (
        /* Scanned result display */
        <div className={`rounded-lg border p-3 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5z" />
                </svg>
              </div>
              <div>
                {showValue && (
                  <p className={`text-sm font-mono font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {scanResult.raw_value}
                  </p>
                )}
                <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {scanResult.format && `${scanResult.format} • `}{new Date(scanResult.timestamp).toLocaleString()}
                </p>
              </div>
            </div>
            {!field.readonly && (
              <button type="button" onClick={clearValue} className="text-xs text-red-500 hover:text-red-400">Clear</button>
            )}
          </div>
          {scanResult.lookup_result != null && (
            <div className={`mt-2 text-xs p-2 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
              <pre className="whitespace-pre-wrap">{JSON.stringify(scanResult.lookup_result, null, 2)}</pre>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Enhanced scanner button — opens QRScannerModal with mode selection */}
          {!scanning && !field.readonly && (
            <button
              type="button"
              onClick={() => setShowEnhancedScanner(true)}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors mb-2 ${
                isDark
                  ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60 border border-blue-700'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
              }`}
            >
              <QrCode className="w-4 h-4" />
              Enhanced QR/Barcode Scanner
            </button>
          )}

          {/* Camera scanner */}
          {scanning && (
            <div className="relative rounded-lg overflow-hidden border border-teal-400">
              <video ref={videoRef} className="w-full h-48 object-cover bg-black" playsInline muted />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 border-2 border-teal-400 rounded-lg opacity-50" />
              </div>
              <button
                type="button"
                onClick={stopCamera}
                className="absolute top-2 right-2 px-2 py-1 text-xs rounded bg-red-500 text-white"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Scan button */}
          {!scanning && (
            <button
              type="button"
              onClick={startScanning}
              disabled={field.readonly}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed text-sm font-medium transition-colors ${
                isDark
                  ? 'border-gray-600 text-gray-400 hover:border-teal-500 hover:text-teal-400'
                  : 'border-gray-300 text-gray-500 hover:border-teal-500 hover:text-teal-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
              </svg>
              Quick Scan (Inline)
            </button>
          )}

          {/* Manual input fallback */}
          <div className="flex gap-2">
            <input
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleManualSubmit(e);
                }
              }}
              placeholder={field.placeholder || 'Or enter code manually...'}
              disabled={field.readonly}
              className={`flex-1 rounded-md px-3 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
            <button
              type="button"
              onClick={handleManualSubmit}
              disabled={!manualInput.trim() || field.readonly}
              className="px-3 py-2 text-sm font-medium rounded-md bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Enter
            </button>
          </div>
        </>
      )}

      {/* Enhanced QRScannerModal — lazy loaded */}
      {showEnhancedScanner && (
        <React.Suspense fallback={<div className="flex items-center justify-center p-8 text-sm text-gray-500">Loading scanner…</div>}>
          <LazyQRScannerModal
            isOpen={showEnhancedScanner}
            mode={enhancedMode}
            onModeChange={setEnhancedMode}
            onScan={handleEnhancedScan}
            onClose={() => setShowEnhancedScanner(false)}
          />
        </React.Suspense>
      )}

      {error && (
        <p className="text-xs text-amber-500 dark:text-amber-400">{error}</p>
      )}
    </div>
  );
};

export default BarcodeScannerField;
